import { getState, allocateId, snapshot } from "../mock/demoAdminState"

function ok(data) {
  return { status: 200, data: snapshot(data) }
}

function delay(ms = 80) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function matchesWhere(record, where) {
  if (!where || typeof where !== "object") return true
  return Object.entries(where).every(([key, value]) => record[key] === value)
}

/**
 * Drop-in stand-in for ModelApi used by the demo build.
 * resource names match the path segments without a leading slash.
 */
class DemoModelApi {
  constructor(resource) {
    this.resource = resource
  }

  async find(id) {
    await delay()
    const list = this._collection()
    const found = list.find((row) => row.id === Number(id) || row.id === id)
    return found ? ok(found) : { status: 404, data: null }
  }

  async all(scope = {}) {
    await delay()
    const state = getState()
    if (this.resource === "redcapApiToken") {
      return ok(state.redcapApiTokens.filter((t) => matchesWhere(t, scope.where)))
    }
    if (this.resource === "substance") {
      return ok(state.substances.filter((s) => matchesWhere(s, scope.where)))
    }
    if (this.resource === "user") {
      return ok(state.users.filter((u) => matchesWhere(u, scope.where)))
    }
    if (this.resource === "project") {
      const projects = state.redcapApiTokens.map((t) => t.project)
      return ok(projects.filter((p) => matchesWhere(p, scope.where)))
    }
    if (this.resource === "otlfbSurvey") {
      const surveys = state.redcapApiTokens
        .map((t) => t.project?.otlfbSurvey)
        .filter(Boolean)
      return ok(surveys.filter((s) => matchesWhere(s, scope.where)))
    }
    if (this.resource === "substanceMode") {
      const modes = state.substances.flatMap((s) => s.substanceModes || [])
      return ok(modes.filter((m) => matchesWhere(m, scope.where)))
    }
    return ok([])
  }

  async save(attrs) {
    await delay()
    if (attrs.id) throw new Error("Can't save persisted record as new.")

    if (this.resource === "otlfbSurvey") {
      return this._saveSurvey(attrs)
    }
    if (this.resource === "substance") {
      return this._saveSubstance(attrs)
    }
    if (this.resource === "substanceMode") {
      return this._saveSubstanceMode(attrs)
    }
    if (this.resource === "user") {
      const state = getState()
      const user = { ...attrs, id: allocateId() }
      state.users.push(user)
      return ok(user)
    }
    if (this.resource === "project" || this.resource === "redcapApiToken") {
      console.log("[FlexTLFB admin demo] Add-project is disabled in demo mode.", attrs)
      return ok({ ...attrs, id: allocateId() })
    }
    return ok({ ...attrs, id: allocateId() })
  }

  async update(attrs) {
    await delay()
    if (!attrs.id) throw new Error("Can't update record which is not yet persisted.")

    if (this.resource === "project") {
      const token = getState().redcapApiTokens.find((t) => t.project?.id === attrs.id)
      if (token?.project) {
        Object.assign(token.project, attrs)
        if (attrs.name) {
          getState().projectInfoByProjectId[attrs.id] = {
            ...getState().projectInfoByProjectId[attrs.id],
            project_title: attrs.name
          }
        }
        return ok(token.project)
      }
    }
    if (this.resource === "user") {
      const state = getState()
      const idx = state.users.findIndex((u) => u.id === attrs.id)
      if (idx >= 0) {
        state.users[idx] = { ...state.users[idx], ...attrs }
        if (state.user.id === attrs.id) {
          state.user = { ...state.user, ...attrs }
        }
        return ok(state.users[idx])
      }
    }
    return ok(attrs)
  }

  async destroy(id) {
    await delay()
    const state = getState()
    const numericId = Number(id)

    if (this.resource === "otlfbSurvey") {
      for (const token of state.redcapApiTokens) {
        if (token.project?.otlfbSurvey?.id === numericId) {
          token.project.otlfbSurvey = null
          return ok({ id: numericId })
        }
      }
    }
    if (this.resource === "project") {
      const before = state.redcapApiTokens.length
      state.redcapApiTokens = state.redcapApiTokens.filter(
        (t) => t.project?.id !== numericId
      )
      if (state.redcapApiTokens.length < before) {
        delete state.projectInfoByProjectId[numericId]
        return ok({ id: numericId })
      }
    }
    if (this.resource === "user") {
      state.users = state.users.filter((u) => u.id !== numericId)
      return ok({ id: numericId })
    }
    return ok({ id: numericId })
  }

  _collection() {
    const state = getState()
    switch (this.resource) {
      case "user":
        return state.users
      case "substance":
        return state.substances
      case "redcapApiToken":
        return state.redcapApiTokens
      case "project":
        return state.redcapApiTokens.map((t) => t.project)
      default:
        return []
    }
  }

  _saveSurvey(attrs) {
    const state = getState()
    const token = state.redcapApiTokens.find(
      (t) => t.project?.id === attrs.projectId
    )
    if (!token?.project) {
      return { status: 404, data: { message: "Project not found" } }
    }

    const surveyId = allocateId()
    const otlfbSurveySubstances = (attrs.otlfbSurveySubstances || []).map((oss) => {
      const substanceMode = {
        ...oss.substanceMode,
        /* SurveyDetails historically reads the truncated include key `dataTyp`. */
        dataTyp: oss.substanceMode?.dataType || oss.substanceMode?.dataTyp
      }
      return {
        id: allocateId(),
        otlfbSurveyId: surveyId,
        substanceId: oss.substanceId,
        substanceModeId: oss.substanceModeId,
        instructionUrl: oss.instructionUrl ?? null,
        substance: snapshot(oss.substance),
        substanceMode: snapshot(substanceMode)
      }
    })

    const survey = {
      id: surveyId,
      projectId: attrs.projectId,
      dayCount: attrs.dayCount,
      otlfbSurveySubstances
    }
    token.project.otlfbSurvey = survey
    console.log("[FlexTLFB admin demo] Survey created in memory:", survey)
    return ok(survey)
  }

  _saveSubstance(attrs) {
    const state = getState()
    const substanceId = allocateId()
    const substanceModes = (attrs.substanceModes || []).map((mode) => ({
      id: allocateId(),
      substanceId,
      name: mode.name,
      dataType: mode.dataType,
      redcapFieldName: mode.redcapFieldName
    }))
    const substance = {
      id: substanceId,
      name: attrs.name,
      substanceModes
    }
    state.substances.push(substance)
    return ok(substance)
  }

  _saveSubstanceMode(attrs) {
    const state = getState()
    const substance = state.substances.find((s) => s.id === attrs.substanceId)
    const mode = {
      id: allocateId(),
      substanceId: attrs.substanceId,
      name: attrs.name,
      dataType: attrs.dataType,
      redcapFieldName: attrs.redcapFieldName
    }
    if (substance) {
      substance.substanceModes = substance.substanceModes || []
      substance.substanceModes.push(mode)
    }
    return ok(mode)
  }
}

export default DemoModelApi
